import { add, subDaysDstSafe } from "~/lib/date";
import { useMemo } from "react";
import { useMetrics } from "~/hooks/useMetrics";
import { interpolateNulls } from "~/lib/interpolateNulls";
import {
  HistoricalDataItem,
  Metrics,
  MetricsEntries,
  parseHistoricalDailyAssetData,
  parseHistoricalDailyFindingData,
} from "~/lib/arrow";

export interface UseDailyCountsParams {
  entityMrn: string;
  metricMrn:
    | Metrics.HistoricalDailyFindingCount
    | Metrics.HistoricalDailyAssetCount;
  numOfDays?: number;
  getCount?: (d: HistoricalDataItem) => bigint | number;
  // Pre-fetched metrics entries. When provided, the hook skips its own
  // GetMetrics request and parses the requested metric out of these entries.
  entries?: MetricsEntries;
}

const dataKeys = {
  [Metrics.HistoricalDailyFindingCount]: "historicalDailyFindingData",
  [Metrics.HistoricalDailyAssetCount]: "historicalDailyAssetData",
} as const;

const entriesParsers = {
  [Metrics.HistoricalDailyFindingCount]: parseHistoricalDailyFindingData,
  [Metrics.HistoricalDailyAssetCount]: parseHistoricalDailyAssetData,
} as const;

export function useDailyCounts({
  entityMrn,
  metricMrn,
  numOfDays = 28,
  getCount,
  entries,
}: UseDailyCountsParams) {
  const { timelineDates, startTime, endTime } = useMemo(
    () => getDailyCountsTimeline(numOfDays),
    [numOfDays],
  );

  const metric = useMetrics({
    entityMrn,
    metricMrns: [metricMrn],
    startTime,
    endTime,
    skip: entries !== undefined,
  });

  const dailyDataKey = dataKeys[metricMrn];
  const metricData = useMemo(
    () =>
      entries !== undefined
        ? entriesParsers[metricMrn](entries)
        : metric[dailyDataKey],
    [entries, metricMrn, metric, dailyDataKey],
  );
  const parsedData = useMemo(
    () => parseDailyCounts(timelineDates, metricData, getCount),
    [timelineDates, metricData, getCount],
  );

  return {
    isLoading: entries !== undefined ? false : metric.isLoading,
    ...parsedData,
  };
}

export function getDailyCountsTimeline(numOfDays: number) {
  // Nexus returns a data bucket for each start of UTC day between date range.
  // We will end at the most recent bucket: the start of current utc day.
  const startOfUtcDayTime = new Date().setUTCHours(0, 0, 0, 0);
  const startOfUtcDay = new Date(startOfUtcDayTime);

  // Sometimes nexus doesn't return a data bucket for some dates. To deal with
  // these holes in the timeline, we generate our own list of dates so that we
  // can identify any missing data buckets from the nexus response.
  const timelineDates = Array.from({ length: numOfDays }, (_, i) => {
    return subDaysDstSafe(startOfUtcDay, numOfDays - 1 - i);
  });

  const startDate = timelineDates[0];
  const endDate = timelineDates[numOfDays - 1];

  const startTime = startDate.toISOString();
  // The endTime input param is not inclusive, so we add a second to ensure
  // nexus includes our range's end date in the returned daily buckets.
  const endTime = add(endDate, { seconds: 1 }).toISOString();

  return { timelineDates, startTime, endTime };
}

function parseDailyCounts(
  dates: Date[],
  metricData: HistoricalDataItem[],
  getCount: UseDailyCountsParams["getCount"],
) {
  const rawData: Array<[date: Date, count: number | null]> = dates.map(
    (date) => {
      // Find the data bucket for this date
      const data = metricData.find(
        (datum) => datum["+timestamp"] === date.getTime(),
      );
      if (!data) {
        // Nexus did not return a data bucket for this date
        return [date, null] as const;
      }

      const count = Number(getCount ? getCount(data) : data.total);

      // Return priority count from this date's data bucket
      return [date, count] as const;
    },
  );

  const interpolatedCounts = interpolateNulls(
    rawData.map(([_, count]) => count),
    { keepPrefixNulls: true, round: true },
  );

  const data = rawData.map(([date], i) => {
    return { date, count: interpolatedCounts[i] };
  });

  const oldestDatum = data.find(({ count }) => count !== null);
  const newestDatum = data.findLast(({ count }) => count !== null);

  const oldestCount = oldestDatum?.count || 0;
  const newestCount = newestDatum?.count || 0;

  const countDelta = newestCount - oldestCount;
  const countDeltaPercentage = Math.round(
    oldestCount !== 0
      ? (countDelta / oldestCount) * 100
      : newestCount === 0
        ? 0
        : newestCount > 0
          ? 100
          : -100,
  );

  return {
    data,
    oldestDatum,
    newestDatum,
    oldestCount,
    newestCount,
    countDelta,
    countDeltaPercentage,
  };
}
